import ReactEChartsCore from 'echarts-for-react/lib/core';
import { PieChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { getColor, rgbaColor } from '@/helpers/utils';
import PropTypes from 'prop-types';
import countSuffix from '@/helpers/countSuffix';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  PieChart,
  CanvasRenderer,
  LegendComponent
]);

type MarketingExpenseItem = {
  value: number;
  name: string;
  percentage?: number;
};

type MarketingExpensesData = {
  tld?: MarketingExpenseItem[];
  language?: MarketingExpenseItem[];
};

type MarketingExpensesChartProps = {
  data?: MarketingExpensesData;
  chartRef?: React.Ref<ReactEChartsCore>;
};

const getOptions = (data?: MarketingExpensesData) => {
  const tldLength = data?.tld?.length || 1;
  const languageLength = data?.language?.length || 1;

  return {
    textStyle: {
      fontFamily: 'dana'
    },
    tooltip: {
      trigger: 'item',
      backgroundColor: getColor('gray-100'),
      textStyle: { color: getColor('gray-1100') },
      formatter: '<strong>{b}</strong><br/> {c} ({d}%)'
    },
    legend: { show: false },
    series: [
      {
        name: 'Tld',
        type: 'pie',
        radius: ['45%', '60%'],
        selectedMode: 'single',
        itemStyle: {
          borderWidth: 2,
          borderColor: getColor('gray-100')
        },
        label: {
          show: false
        },
        labelLine: { show: false },
        data: [
          ...(data?.tld ?? []).map((data, index) => ({
            value: data.value,
            name: data.name,
            itemStyle: {
              color: rgbaColor(getColor('primary'), (index + 1) / tldLength)
            }
          }))
        ]
      },
      {
        name: 'language',
        type: 'pie',
        radius: ['70%', '75%'],
        barWidth: 10,
        itemStyle: {
          borderWidth: 2,
          borderColor: getColor('gray-100')
        },
        label: {
          formatter: '{per|{d}%}',
          rich: {
            per: {
              fontSize: 14,
              fontWeight: 'bold',
              lineHeight: 33
            }
          }
        },
        labelLine: { show: false },
        data: [
          ...(data?.language ?? []).map((data, index) => ({
            value: data.value,
            name: data.name,
            percentage: data.percentage,
            itemStyle: {
              color: rgbaColor(getColor('info'), (index + 1) / languageLength)
            },
            label: {
              rich: {
                per: {
                  color: rgbaColor(
                    getColor('info'),
                    (index + 1) / languageLength
                  )
                }
              }
            }
          }))
        ]
      }
    ]
  };
};

export const MARKETING_CHART_MIN_HEIGHT = '18.75rem';

const MarketingExpensesChart = ({
  data,
  chartRef
}: MarketingExpensesChartProps) => {
  return (
    <div className="position-relative py-2">
      <ReactEChartsCore
        ref={chartRef}
        echarts={echarts}
        option={getOptions(data)}
        style={{ minHeight: MARKETING_CHART_MIN_HEIGHT }}
      />
      <div className="position-absolute top-50 start-50 translate-middle text-center">
        <div className="rounded-circle d-flex flex-center marketing-exp-circle">
          <h4 className="mb-0 text-900">
            {countSuffix(
              data?.language?.reduce((sum, item) => sum + item.value, 0)
            )}
          </h4>
        </div>
      </div>
    </div>
  );
};

MarketingExpensesChart.propTypes = {
  data: PropTypes.shape({
    tld: PropTypes.array,
    language: PropTypes.array
  })
};

export default MarketingExpensesChart;
